import { useIdentity } from '@vp/auth-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api-config';
import { IApiHooksWithTriggerData, IIdentityObject } from '../../interfaces/app-interfaces';
import { clearChatbotToken, clearChatSessionType, clearRestId, getChatbotToken, getRestId } from '../../utils/session-storage-utils';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { CAContext } from '../../contexts/CAContext';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { useCustomTracking } from '../custom-hooks/useTracking';

export const useCBChatEnd = (): IApiHooksWithTriggerData<string, { escalate?: boolean }> => {
  const { sendTrackingEvent } = useCustomTracking();
  const identity = useIdentity();

  const { setChatState } = useContext(CAContext);
  const { setState: setChatbotState } = useContext(ChatbotContext);

  const { logCustomAttributesNR } = useNRLogging();

  const [data, setData] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [requestData, setRequestData] = useState<{ escalate: boolean }>();

  const trigger = useCallback((data: { escalate: boolean }) => {
    setRequestData({ escalate: data.escalate || false });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (requestData) {
        try {
          setLoading(true);
          const { authorizationHeader, canonicalId } = identity as IIdentityObject;
          const chatbotToken = getChatbotToken();
          const myHeaders = new Headers();
          myHeaders.append('Authorization', authorizationHeader || '');
          myHeaders.append('Content-Type', 'application/json');
          myHeaders.append('Chatbot-Authorization', `Bearer ${chatbotToken}`);

          const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify({
              escalate: requestData?.escalate || false,
              request_id: getRestId() || '',
              shopper_id: canonicalId || '',
            }),
            redirect: 'follow' as RequestRedirect,
          };
          logCustomAttributesNR('ca-chatbot-api-request', {
            logLevel: 'info',
            message: 'Ending chatbot session',
            escalate: requestData?.escalate || false,
            apiType: 'endChatbotSession',
          });
          sendTrackingEvent('Chatbot Clicked', 'Chatbot:Session', 'chatSessionClosed');
          fetch(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/session-end`, requestOptions)
            .then((response) => {
              if (!response.ok) {
                // If response is not ok (status not in the range 200-299),
                // throw an error to skip the next then block
                const error = new Error(`API responded with status: ${response.status}`);
                error.name = 'ApiError';
                throw error;
              }
              return response.json();
            })
            .then((result) => {
              console.log(result);
              setData(`Session ended successfully ${result.message}`);
              setChatbotState((prev) => ({ ...prev, endChatData: `Session ended successfully ${result.message}` }));
              setError(undefined);
              clearChatbotToken();
              logCustomAttributesNR('ca-chatbot-api-response', {
                logLevel: 'info',
                message: 'Ending chatbot session API success',
                apiType: 'endChatbotSession',
              });
            })
            .catch((error) => {
              setError(new Error(`Failed to end session`));
              console.error(error);
              logCustomAttributesNR('ca-chatbot-api-error', {
                errorName: error.name,
                errorStack: error.stack,
                message: error.message,
                logLevel: 'error',
                apiType: 'endChatbotSession',
              });
            })
            .finally(() => {
              // Reset loading state and requestData after completion
              setLoading(false);
              setRequestData(undefined);

              // Reset chatbot state and session storage
              setChatbotState((prevState) => ({
                ...prevState,
                conversationMessages: [],
                discussionId: '',
                sessionId: '',
                showErrorMessage: false,
              }));
              clearChatbotToken();
              clearChatSessionType();
              clearRestId();
              // Reset chat state
              setChatState((prevState) => ({
                ...prevState,
                // chatWindow: 'minimized',
                chatSession: 'endChatSession',
                isPrivacyPolicyAccepted: false,
                unreadMessages: 0,
              }));
            });
        } catch (err) {
          setError(err as Error);
        }
      }
    };
    fetchData();
  }, [requestData]);

  return {
    data,
    loading,
    error,
    trigger,
  };
};
